import { Suspense } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'

import AppErrorBoundaryFallback from './common/AppErrorBoundaryFallback'
import GovernanceGuard from './common/guards/GovernanceGuard'
import MultisigGuard from './common/guards/MultisigGuard'
import MainLayout from './layouts/MainLayout'
import ProposalDetailPage from './pages/Governance/ProposalDetailPage'
import ProposalsListPage from './pages/Governance/ProposalsListPage'
import Home from './pages/Home'
import Multisig from './pages/Multisig'
import MultisigCreateProposal, {
  CreateProposalConstructor,
  CreateProposalManual,
  MultisigCreateProposalRedirect,
} from './pages/Multisig/CreateProposal'
import MultisigGroup from './pages/Multisig/Group'
import MultisigProposal from './pages/Multisig/Proposal'

export enum RoutePaths {
  Root = '/',

  Multisig = '/multisig',
  MultisigGroup = '/multisig/groups/:account',
  MultisigCreateProposal = '/multisig/groups/:account/create-proposal',
  MultisigCreateProposalManual = '/multisig/groups/:account/create-proposal/manual',
  MultisigCreateProposalConstructor = '/multisig/groups/:account/create-proposal/constructor',
  MultisigGroupProposal = '/multisig/groups/:account/proposals/:proposalId',

  Governance = '/governance',
  GovernanceProposal = '/governance/proposals/:proposalId',
}

enum MultisigSegments {
  Groups = 'groups',
  Proposals = 'proposals',
  CreateProposal = 'create-proposal',
}

enum CreateProposalSegments {
  Manual = 'manual',
  Constructor = 'constructor',
}

export const createRouter = () =>
  createBrowserRouter(
    [
      {
        path: RoutePaths.Root,
        ErrorBoundary: () => (
          <AppErrorBoundaryFallback onReset={() => window.location.reload()} />
        ),
        element: (
          <MainLayout>
            <Suspense fallback={null}>
              <Outlet />
            </Suspense>
          </MainLayout>
        ),

        children: [
          { index: true, element: <Home /> },

          {
            path: 'multisig',
            element: <MultisigGuard />,
            children: [
              { index: true, element: <Multisig /> },

              {
                path: `${MultisigSegments.Groups}/:account`,
                element: <MultisigGroup />,
              },

              {
                path: `${MultisigSegments.Groups}/:account/${MultisigSegments.CreateProposal}`,
                element: <MultisigCreateProposal />,
                children: [
                  {
                    index: true,
                    element: <MultisigCreateProposalRedirect />,
                  },
                  {
                    path: CreateProposalSegments.Manual,
                    element: <CreateProposalManual />,
                  },
                  {
                    path: CreateProposalSegments.Constructor,
                    element: <CreateProposalConstructor />,
                  },
                ],
              },

              {
                path: `${MultisigSegments.Groups}/:account/${MultisigSegments.Proposals}/:proposalId`,
                element: <MultisigProposal />,
              },
            ],
          },

          {
            path: 'governance',
            element: <GovernanceGuard />,
            children: [
              { index: true, element: <ProposalsListPage /> },
              {
                path: 'proposals/:proposalId',
                element: <ProposalDetailPage />,
              },
            ],
          },

          {
            path: '*',
            element: <Navigate replace to={RoutePaths.Root} />,
          },
        ],
      },
    ],
    { basename: '/shards' },
  )
